import React, { useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { FaCheck } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import './Header.css';

const FORM_TOKEN_KEY = 'gw_form_ok_until'; // short-lived token for /confirmation access

const Header = () => {
  const backgroundUrl = `${process.env.PUBLIC_URL}/header-image.jpg`;
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    details: ''
  });

  const [consentAccepted, setConsentAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'consent' && type === 'checkbox') {
      setConsentAccepted(checked);
      if (checked) setError(null);
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!consentAccepted) {
      setError('Please check the consent box to proceed.');
      return;
    }

    if (!captchaToken) {
      setError('Please verify you are not a robot.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch('https://formspree.io/f/mldljllb', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          consent: consentAccepted ? 'yes' : 'no',
          'g-recaptcha-response': captchaToken,
        }),
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          address: '',
          details: '',
        });
        setCaptchaToken(null);
        setConsentAccepted(false);

        // allow /confirmation for 2 minutes
        const expiresAt = Date.now() + 2 * 60 * 1000;
        sessionStorage.setItem(FORM_TOKEN_KEY, String(expiresAt));

        setTimeout(
          () => navigate('/confirmation', { replace: true, state: { from: 'form' } }),
          800
        );
      } else {
        const { errors } = await response.json();
        throw new Error(errors?.[0]?.message || 'Formspree error');
      }
    } catch (err) {
      console.error(err);
      setError('Oops! Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <header className="header">
      <div
        className="hero"
        style={{ backgroundImage: `url(${backgroundUrl})` }}
      >
        <div className="hero-overlay" />
        <div className="hero-particles" />

        <div className="container hero-container">
          {/* The grid is explicitly top-aligned so the headline and form start at the same vertical line */}
          <div className="hero-content hero-align-top">
            <div className="hero-text align-start">
              <span className="hero-badge">
                <span>Welcome to Greenworks Carpet Cleaning</span>
              </span>

              <h1>
                <span className="hero-title-nowrap">
                  <span className="highlight-text">#1&nbsp;Rated</span>&nbsp;Carpet
                </span>{' '}
                Cleaning&nbsp;Service
                <br />
                in&nbsp;Your&nbsp;Area
              </h1>

              <p className="hero-description">
                At&nbsp;Greenworks Carpet Cleaning, a&nbsp;home isn&apos;t spotless until
                its carpets are. As an environmentally-conscious company, we
                deliver unparalleled upholstery and carpet-cleaning services you
                can trust.
              </p>

              <div className="hero-features">
                {[
                  'Eco-Friendly Products',
                  'Same Day Service',
                  '100% Trust Guarantee',
                ].map((label) => (
                  <div className="feature-item" key={label}>
                    <div className="feature-icon">
                      <FaCheck />
                    </div>
                    <span>{label}</span>
                  </div>
                ))}
              </div>

              <div className="hero-cta">
                <Link to="/contact" className="cta-primary">
                  <span>Schedule&nbsp;Service</span>
                </Link>
                <Link to="/about" className="cta-secondary">
                  <span>Learn&nbsp;More</span>
                  <div className="hover-effect" />
                </Link>
              </div>
            </div>

            <div className="hero-form-container align-start">
              <div className="form-decoration form-decoration-1" />
              <div className="form-decoration form-decoration-2" />

              <form className="booking-form" onSubmit={handleSubmit} noValidate>
                <div className="form-header">
                  <h3>
                    Request a <span className="highlight-text">Free&nbsp;Quote</span>
                  </h3>
                </div>

                <div className="form-fields-grid">
                  <div className="form-group">
                    <input
                      name="fullName"
                      type="text"
                      placeholder="Full Name"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      aria-label="Full Name"
                    />
                  </div>

                  <div className="form-group">
                    <input
                      name="email"
                      type="email"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      aria-label="Email Address"
                    />
                  </div>

                  <div className="form-group">
                    <input
                      name="phone"
                      type="tel"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      aria-label="Phone Number"
                    />
                  </div>

                  <div className="form-group">
                    <input
                      name="address"
                      type="text"
                      placeholder="Address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      aria-label="Address"
                    />
                  </div>
                </div>

                <div className="form-group form-group--details">
                  <textarea
                    name="details"
                    placeholder="Additional Details (Optional)"
                    value={formData.details}
                    onChange={handleChange}
                    aria-label="Additional Details"
                  />
                </div>

                <div className="consent-row">
                  <input
                    id="consent"
                    name="consent"
                    type="checkbox"
                    checked={consentAccepted}
                    onChange={handleChange}
                    required
                    aria-required="true"
                    className="consent-checkbox"
                  />
                  <label htmlFor="consent" className="consent-label">
                    I consent to being contacted via SMS or Email by Greenworks.
                  </label>
                </div>

                <div className="form-captcha">
                  <ReCAPTCHA
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    onChange={handleCaptchaChange}
                  />
                </div>

                {error && <div className="form-error" role="alert">{error}</div>}

                <button
                  type="submit"
                  className="submit-btn"
                  disabled={isSubmitting || !consentAccepted}
                  aria-disabled={isSubmitting || !consentAccepted}
                >
                  {isSubmitting ? (
                    <span>Submitting...</span>
                  ) : (
                    <>
                      <span>Get Free Estimate</span>
                    </>
                  )}
                  <div className="submit-hover-effect" />
                </button>

                {submitSuccess && (
                  <p className="form-success" role="status">
                    Thanks! Redirecting to confirmation…
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
